import React, { useContext, useMemo } from 'react';

import { mergePendingParamsIntoSearch } from '@aline/common';
import type { Navigator } from 'react-router';
import { NavigationType, UNSAFE_LocationContext, UNSAFE_NavigationContext } from 'react-router';
import { parsePath } from 'react-router-dom';

import { CurrentTabIdProvider } from './tab-active-context';
import type { Tab } from './tab-types';

/**
 * A no-op navigator for hidden tabs.
 *
 * Hidden tabs stay mounted, so their components can still hold callbacks that
 * call useNavigate(). Those calls should not change the visible browser URL.
 */
const FROZEN_NAVIGATOR: Navigator = {
  createHref: to => (typeof to === 'string' ? to : (to.pathname ?? '/')),
  go: () => {},
  push: () => {},
  replace: () => {},
};

interface TabRouterProviderProps {
  children: React.ReactNode;
  isActive: boolean;
  tab: Tab;
}

function createLocationValue(route: string, tabId: string, pendingParams?: Record<string, string>) {
  const parsed = parsePath(route);
  return {
    location: {
      hash: parsed.hash || '',
      key: tabId,
      pathname: parsed.pathname || '/',
      search: mergePendingParamsIntoSearch(parsed.search || '', pendingParams),
      state: null,
    },
    navigationType: NavigationType.Pop,
  };
}

/**
 * Provides router context isolation for each tab.
 *
 * Descendants using `useLocation()` see this tab's durable route merged with any
 * runtime-only `pendingParams` (one-shot deep links), not the active browser
 * location alone. Rebuilding the value when `tab.route` / `pendingParams` change
 * keeps pathname, search, and hash reactive.
 *
 * Navigation context differs: the active tab gets the real navigator; inactive
 * tabs get a no-op navigator so hidden content cannot change the browser URL.
 *
 * The tab's id is published alongside the location so any subtree holding a tab's
 * router context can also consume that tab's pending params. The global right
 * drawer renders outside the tab activity area and would otherwise have no id.
 */
export function TabRouterProvider({
  children,
  isActive,
  tab,
}: TabRouterProviderProps): JSX.Element {
  const realNavContext = useContext(UNSAFE_NavigationContext);
  const locationValue = useMemo(
    () => createLocationValue(tab.route, tab.id, tab.pendingParams),
    [tab.route, tab.id, tab.pendingParams]
  );
  const frozenNavContext = useMemo(
    () => ({
      basename: '/',
      future: { v7_relativeSplatPath: false },
      navigator: FROZEN_NAVIGATOR,
      static: false,
    }),
    []
  );
  const navValue = isActive ? realNavContext : frozenNavContext;

  return (
    <CurrentTabIdProvider value={tab.id}>
      <UNSAFE_LocationContext.Provider value={locationValue}>
        <UNSAFE_NavigationContext.Provider value={navValue}>
          {children}
        </UNSAFE_NavigationContext.Provider>
      </UNSAFE_LocationContext.Provider>
    </CurrentTabIdProvider>
  );
}
